import { IconChevronRight } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import {
  Button,
  Missing,
  DocumentActionsCell,
  getCheckboxColumnDefs,
  TextCell,
} from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ProductResponses } from "@tfg/types/endpoints/products"

import { TranslatedTextContent } from "~/components/TranslatedTextContent.tsx"
import { CertificatesCell } from "~/components/cells/CertificatesCell.tsx"
import { fetchDocumentUrl } from "~/hooks/useDocumentsQuery"
import { CompositionCell } from "~/pages/Brand/Regulations/RDUE/CompositionCell.tsx"

const columnHelper = createColumnHelper<ProductResponses.RDUEProduct>()
export const useRdueColumns = (
  maxComponentCount: number,
  viewComponentDetails: (component: ProductResponses.RDUEComponent) => void
) => {
  const { t } = useTranslation(["regulations", "common"])

  return useMemo<ColumnDef<ProductResponses.RDUEProduct, any>[]>(
    () => [
      getCheckboxColumnDefs<ProductResponses.RDUEProduct>(),
      columnHelper.accessor(() => "documents", {
        id: "actions-cell-documents",
        header: t("regulations:columns.rdue.documents"),
        cell: ({ row }) =>
          row.original.dueDiligenceDocumentId ? (
            <DocumentActionsCell
              documentName={t("regulations:columns.rdue.dueDiligence")}
              documentId={row.original.dueDiligenceDocumentId}
              fetchDownloadUrl={() =>
                fetchDocumentUrl(row.original.dueDiligenceDocumentId, true)
              }
              fetchViewUrl={() =>
                fetchDocumentUrl(row.original.dueDiligenceDocumentId)
              }
              missingDocumentText={t("common:documentUpload.missingDocument")}
            />
          ) : (
            <Missing />
          ),
      }),
      columnHelper.accessor("name", {
        header: t("regulations:columns.rdue.name"),
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("rdueCompliance", {
        header: t("regulations:columns.rdue.compliant"),
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("brandReference", {
        header: t("regulations:columns.rdue.brandReference"),
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("purchaseOrder", {
        header: t("regulations:columns.rdue.purchaseOrder"),
        cell: (info) => {
          return <TextCell value={info.getValue()?.reference} />
        },
      }),
      columnHelper.accessor("quantity", {
        header: t("regulations:columns.rdue.quantity"),
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("composition", {
        header: t("regulations:columns.rdue.composition"),
        cell: ({ getValue }) => <CompositionCell composition={getValue()} />,
      }),
      columnHelper.accessor("relevantCommodity", {
        header: t("regulations:columns.rdue.relevantCommodity"),
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("hsCode", {
        header: t("regulations:columns.rdue.hsCode"),
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      ...Array.from({ length: maxComponentCount }).flatMap((_, index) => {
        return [
          columnHelper.accessor(`components`, {
            header: t("regulations:columns.rdue.component_details", {
              index: (index + 1).toString(),
            }),
            id: `component_details-${index}`,
            cell: (info) => {
              const components = info.getValue()
              const component =
                components.find(
                  (component) => component.number === index + 1
                ) ?? (components.length > index ? components[index] : undefined)
              return component ? (
                <Button
                  onClick={() => {
                    viewComponentDetails(component)
                  }}
                  variant="tertiary"
                  color="black"
                  fw="normal"
                  w={200}
                  px={0}
                  justify="space-between"
                  rightSection={<IconChevronRight size={16} />}
                >
                  <TranslatedTextContent
                    textContent={component.typeTextContent}
                  />
                </Button>
              ) : (
                <Missing />
              )
            },
          }),
          columnHelper.accessor(`components`, {
            header: t("regulations:columns.rdue.component_certificates", {
              index: (index + 1).toString(),
            }),
            id: `component_certificates-${index}`,
            cell: (info) => {
              const components = info.getValue()
              const component =
                components.find(
                  (component) => component.number === index + 1
                ) ?? (components.length > index ? components[index] : undefined)
              return component ? (
                <CertificatesCell
                  certificates={component.certificates}
                  key={index}
                />
              ) : (
                <Missing />
              )
            },
          }),
        ]
      }),
    ],
    [t, maxComponentCount, viewComponentDetails]
  )
}
